// ─── Currency Service ─────────────────────────────────────────────
// Primary: github.com/fawazahmed0/exchange-api (free, no key, updated daily, accurate)
// Fallback: Frankfurter API
// Both are free and don't require API keys

let ratesCache = null;
let ratesCacheTime = 0;
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

export const CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$", flag: "🇺🇸" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$", flag: "🇧🇷" },
  { code: "EUR", name: "Euro", symbol: "€", flag: "🇪🇺" },
  { code: "GBP", name: "British Pound", symbol: "£", flag: "🇬🇧" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥", flag: "🇯🇵" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$", flag: "🇨🇦" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$", flag: "🇦🇺" },
  { code: "MXN", name: "Mexican Peso", symbol: "MX$", flag: "🇲🇽" },
  { code: "KRW", name: "South Korean Won", symbol: "₩", flag: "🇰🇷" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥", flag: "🇨🇳" },
];

/**
 * Fetch exchange rates from USD base
 * Uses fawazahmed0 API (most accurate free source) with Frankfurter as fallback
 */
export async function fetchExchangeRates() {
  const now = Date.now();
  if (ratesCache && (now - ratesCacheTime) < CACHE_TTL) {
    return ratesCache;
  }

  // Try primary API: fawazahmed0/exchange-api (CDN-based, very fast)
  try {
    const response = await fetch("https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.min.json");
    if (response.ok) {
      const data = await response.json();
      if (data.usd) {
        const rates = { USD: 1 };
        CURRENCIES.forEach((c) => {
          const key = c.code.toLowerCase();
          if (data.usd[key]) rates[c.code] = data.usd[key];
        });
        ratesCache = rates;
        ratesCacheTime = now;
        return rates;
      }
    }
  } catch (err) {
    console.warn("Primary currency API failed, trying fallback:", err.message);
  }

  // Fallback: Frankfurter API
  try {
    const quotes = CURRENCIES.map((c) => c.code).filter((c) => c !== "USD").join(",");
    const response = await fetch(`https://api.frankfurter.dev/v2/rates?base=USD&quotes=${quotes}`);
    if (response.ok) {
      const data = await response.json();
      const ratesData = Array.isArray(data) ? data[0]?.rates || {} : data.rates || {};
      ratesData.USD = 1;
      ratesCache = ratesData;
      ratesCacheTime = now;
      return ratesData;
    }
  } catch (err) {
    console.warn("Fallback currency API also failed:", err.message);
  }

  // Last resort: return cached or hardcoded fallback
  return ratesCache || { USD: 1, BRL: 5.16, EUR: 0.87, GBP: 0.77, JPY: 146, CAD: 1.38, AUD: 1.50, MXN: 19.7, KRW: 1370, CNY: 7.17 };
}

/**
 * Format a price in the given currency
 */
export function formatCurrency(amount, currencyCode) {
  if (amount === 0 || amount == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: currencyCode === "JPY" || currencyCode === "KRW" ? 0 : 2,
      maximumFractionDigits: currencyCode === "JPY" || currencyCode === "KRW" ? 0 : 2,
    }).format(amount);
  } catch {
    const curr = CURRENCIES.find((c) => c.code === currencyCode);
    return `${curr?.symbol || "$"}${amount.toFixed(2)}`;
  }
}

export function getCurrencyInfo(code) {
  return CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];
}
